// src/components/post/CommentItem.jsx
import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactTimeAgo from 'react-time-ago';
import { FaHeart, FaRegHeart, FaReply, FaEllipsisH, FaPen, FaTrash } from 'react-icons/fa';

import { UserContext } from '../../context/userContext';
import CommentForm from './CommentForm';

const CommentItem = ({ comment, postId, onReplyAdded, onCommentUpdated, onCommentDeleted }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isLiked, setIsLiked] = useState(comment?.liked || false);
  const [likeCount, setLikeCount] = useState(comment?.likes || 0);
  const [showOptions, setShowOptions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  
  const { currentUser } = useContext(UserContext);
  
  const isAuthor = currentUser && comment.user._id === currentUser.id;

  // Toggle like on comment
  const toggleLike = async () => {
    if (!currentUser) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/likes`,
        {
          targetId: comment._id,
          targetType: 'comment',
          reaction: 'like'
        },
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );

      const { liked } = response.data;
      setIsLiked(liked);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
      
      if (onCommentUpdated) {
        onCommentUpdated({
          ...comment,
          likes: liked ? comment.likes + 1 : comment.likes - 1,
          liked
        });
      }
    } catch (error) {
      console.error('Error toggling like on comment:', error);
    }
  };

  // Handle reply form submission
  const handleReplyAdded = (newReply) => {
    setShowReplyForm(false);
    if (onReplyAdded) {
      onReplyAdded(newReply);
    }
  };

  // Handle edit comment
  const submitEdit = async () => {
    if (!currentUser || !isAuthor) return;
    
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/comments/${comment._id}`,
        { text: editText },
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      setIsEditing(false);
      
      if (onCommentUpdated) {
        onCommentUpdated({
          ...comment,
          text: editText,
          isEdited: true
        });
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  // Handle delete comment
  const deleteComment = async () => {
    if (!currentUser || !isAuthor) return;
    
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_BASE_URL}/comments/${comment._id}`,
          {
            headers: { Authorization: `Bearer ${currentUser.token}` }
          }
        );
        
        if (onCommentDeleted) {
          onCommentDeleted(comment._id);
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  return (
    <div className="flex gap-3 mb-6 group">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <Link to={`/profile/${comment.user._id}`} className="block w-10 h-10 rounded-full overflow-hidden">
          <img 
            src={`${process.env.REACT_APP_ASSETS_URL}/uploads/profile/${comment.user.avatar}`} 
            alt={`${comment.user.name}`}
            className="w-full h-full object-cover"
          />
        </Link>
      </div>
      
      {/* Comment content */}
      <div className="flex-grow">
        <div className="bg-white p-3 rounded-lg shadow-sm w-full">
          {/* Comment header */}
          <div className="flex justify-between items-center mb-2">
            <Link to={`/profile/${comment.user._id}`} className="font-medium text-gray-900 hover:underline">
              {comment.user.name}
            </Link>
            
            <div className="text-xs text-gray-500 flex items-center">
              <ReactTimeAgo date={new Date(comment.createdAt)} locale="en-US" className="text-gray-500" />
              {comment.isEdited && <span className="ml-1 text-gray-400">(edited)</span>}
            </div>
          </div>
          
          {/* Comment body */}
          {isEditing ? (
            <div className="mb-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-2">
                <button 
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 text-sm rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={submitEdit} 
                  disabled={!editText.trim()}
                  className="px-3 py-1 text-sm rounded-md bg-primary text-white hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-800 mb-2">{comment.text}</p>
          )}
          
          {/* Comment media */}
          {comment.media && (
            <div className="mb-2 rounded-md overflow-hidden">
              <img 
                src={`${process.env.REACT_APP_ASSETS_URL}/uploads/media/${comment.media}`} 
                alt="Comment attachment" 
                className="max-w-full h-auto"
              />
            </div>
          )}
        </div>
        
        {/* Comment actions */}
        <div className="flex items-center gap-4 mt-1 ml-1">
          <button 
            className={`flex items-center gap-1 text-xs ${isLiked ? 'text-red' : 'text-gray-500'} hover:text-red transition-colors`} 
            onClick={toggleLike}
          >
            {isLiked ? <FaHeart className="text-red" /> : <FaRegHeart />}
            {likeCount > 0 && <span>{likeCount}</span>}
          </button>
          
          <button 
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary transition-colors" 
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            <FaReply />
            <span>Reply</span>
          </button>
          
          {isAuthor && (
            <div className="relative">
              <button 
                className="flex items-center text-xs text-gray-500 hover:text-primary transition-colors" 
                onClick={() => setShowOptions(!showOptions)}
              >
                <FaEllipsisH />
              </button>
              
              {showOptions && (
                <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg py-1 z-10">
                  <button 
                    onClick={() => {
                      setIsEditing(true);
                      setShowOptions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaPen className="text-gray-500" size={12} /> Edit
                  </button>
                  <button 
                    onClick={deleteComment}
                    className="w-full text-left px-4 py-2 text-sm text-red hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaTrash className="text-red" size={12} /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Reply form */}
        {showReplyForm && (
          <div className="mt-3">
            <CommentForm 
              postId={postId} 
              parentCommentId={comment._id}
              onCommentAdded={handleReplyAdded} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentItem;